import { Injectable, Inject, PLATFORM_ID, OnDestroy, effect } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../environments/environment';
import { ANALYTICS_CONFIG } from '../config/analytics.config';
import { AnalyticsSession, AnalyticsEvent } from '../interfaces/analytics.interfaces';
import { WebsiteStateService } from '../state-management/services/website-state.service';
import { CloudFunctionsService } from './cloud-functions.service';
import { LoggerService } from './logger.service';


interface SimpleAttribution {
  gclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer: string;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements OnDestroy {
  private currentSession: AnalyticsSession | null = null;

  private engagementTracker = {
    sessionStartTime: 0,
    maxScrollPercentage: 0,
    isEngaged: false,
    engagementSent: false,
    timeThreshold: ANALYTICS_CONFIG.engagementTimeoutMilliSec,
    scrollThreshold: ANALYTICS_CONFIG.engagementScrollThreshold
  };

  private engagementTimers: { [key: string]: NodeJS.Timeout } = {};

  constructor(
    private cloudFunctionsService: CloudFunctionsService,
    private logger: LoggerService,
    private websiteStateService: WebsiteStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (this.isInactive()) return;
    this.setupConsentWatcher();
    this.initializeAnalyticsAsync();
  }

  private async initializeAnalyticsAsync(): Promise<void> {
    try {
      await new Promise(resolve => setTimeout(resolve, 0));
      this.initializeCurrentSession();

      if (this.currentSession) {
        await new Promise(resolve => setTimeout(resolve, 10));

        this.startSession();
        this.initializeEngagementTracking();

        this.logger.log('📊 Analytics: Service initialized without blocking app');
      }
    } catch (error) {
      this.logger.warn('📊 Analytics: Non-critical initialization error:', error);
    }
  }


  private isInactive() {
    return (!isPlatformBrowser(this.platformId) || environment.isAdminPage);
  }


  // === PUBLIC TRACKING METHODS ===
  public trackCustomEvent(eventName: string, eventData: Partial<AnalyticsEvent>): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.websiteStateService.addBufferedEvent({
      id: this.generateBufferedEventId(),
      type: 'custom_event',
      eventName: eventName,
      eventData: eventData,
      timestamp: new Date().toISOString(),
      processed: false
    });
    this.logger.log('📊 Analytics: Custom event buffered:', eventName);
  }

  // === CONVENIENCE METHODS ===
  public trackPageView(page?: string): void {
    if (this.isInactive()) return

    const currentPage = page || window.location.pathname;
    this.trackCustomEvent('page_view', {
      eventType: 'page_view',
      page: currentPage,
      isConversion: false,
      customData: {
        page_title: document.title,
        referrer: document.referrer,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }
    });

    // Engagement tracking läuft global für die gesamte Session
    // Kein Restart bei page views
  }

  public trackClick(elementId: string, page?: string): void {
    this.trackCustomEvent('click', {
      eventType: 'click',
      elementId,
      page: page || window.location.pathname,
      isConversion: false
    });
  }

  public trackContactIntent(method: 'whatsapp' | 'phone' | 'email' | 'directions' | 'contact_form', elementId: string, trackingData?: any): void {
    const intentLevels = {
      'phone': 'very_high',
      'whatsapp': 'very_high',
      'directions': 'very_high',
      'contact_form': 'very_high',
      'email': 'high'
    } as const;

    this.trackCustomEvent('contact_intent', {
      eventType: 'contact_intent',
      elementId,
      isConversion: false,
      contactMethod: method,
      intentLevel: intentLevels[method],
      customData: {
        timestamp: new Date().toISOString(),
        conversion_potential: true,
        ...trackingData
      }
    });
  }

  public trackOnlinePurchase(orderValue: number, orderId: string, products?: string[]): void {
    this.trackCustomEvent('online_purchase', {
      eventType: 'conversion',
      isConversion: true,
      conversionType: 'online',
      conversionAction: 'purchase',
      conversionValue: orderValue,
      customData: {
        purchase_type: 'online_shop',
        order_id: orderId,
        products: products,
        verified_purchase: true,
        timestamp: new Date().toISOString()
      }
    });
  }

  private initializeEngagementTracking(): void {
    if (this.isInactive()) return;

    // Nur initialisieren wenn noch nicht gestartet
    if (this.engagementTracker.sessionStartTime > 0) {
      this.logger.log('📊 Analytics: Engagement tracking already active for this session');
      return;
    }

    try {
      // Setze Session-Startzeit (nur einmal pro Session)
      this.engagementTracker.sessionStartTime = Date.now();
      this.engagementTracker.maxScrollPercentage = 0;
      this.engagementTracker.isEngaged = false;
      this.engagementTracker.engagementSent = false;

      // Setup scroll tracking (global für alle Seiten)
      this.setupScrollTracking();

      // Setup time-based engagement tracking (einmal pro Session)
      this.setupTimeEngagement();

      this.logger.log('📊 Analytics: Global session engagement tracking initialized');
    } catch (error) {
      this.logger.warn('📊 Analytics: Engagement tracking initialization failed (non-critical)', error);
    }
  }

  private setupScrollTracking(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const handleScroll = () => {
      try {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercentage = documentHeight > 0 ? (scrollTop / documentHeight) * 100 : 0;

        // Update max scroll percentage
        this.engagementTracker.maxScrollPercentage = Math.max(
          this.engagementTracker.maxScrollPercentage,
          scrollPercentage
        );

        // Check if scroll threshold reached
        if (!this.engagementTracker.isEngaged &&
          scrollPercentage >= this.engagementTracker.scrollThreshold) {
          this.markEngagement('scroll', { scrollPercentage });
        }
      } catch (error) {
        this.logger.warn('📊 Analytics: Scroll tracking error (non-critical)', error);
      }
    };

    // Passive listener for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('scroll', handleScroll);
    });
  }

  private setupTimeEngagement(): void {
    const timerId = setTimeout(() => {
      if (!this.engagementTracker.isEngaged) {
        const timeSpent = Date.now() - this.engagementTracker.sessionStartTime;
        this.markEngagement('time', { timeSpent });
      }
    }, this.engagementTracker.timeThreshold);

    // Store timer for cleanup
    this.engagementTimers['timeEngagement'] = timerId;

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
      if (this.engagementTimers['timeEngagement']) {
        clearTimeout(this.engagementTimers['timeEngagement']);
      }
    });
  }

  private markEngagement(trigger: 'scroll' | 'time', data: any): void {
    if (this.engagementTracker.isEngaged || this.engagementTracker.engagementSent) return;

    this.engagementTracker.isEngaged = true;

    const engagementData = {
      trigger,
      totalSessionTime: Date.now() - this.engagementTracker.sessionStartTime,
      maxScrollPercentage: this.engagementTracker.maxScrollPercentage,
      ...data
    };

    // Event wird nur bei Consent gesendet (buffered wenn kein Consent)
    this.trackCustomEvent('engaged_session', {
      eventType: 'engagement',
      isConversion: false,
      customData: {
        event_subtype: 'engaged_session',
        engagement_trigger: trigger,
        total_session_time_ms: engagementData.totalSessionTime,
        max_scroll_percentage: engagementData.maxScrollPercentage,
        timestamp: new Date().toISOString(),
        ...engagementData
      }
    });

    this.logger.log('📊 Analytics: Session marked as engaged (global)', { trigger, data: engagementData });
  }

  private initializeCurrentSession(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Sichere Initialisierung der Session-Daten
    const { visitorId, sessionId } = this.getOrCreateSessionIdentifiers();
    const attribution = this.getSimpleAttribution();
    const originalGclid = this.getOrSetOriginalGclid(attribution.gclid);

    this.currentSession = {
      sessionId,
      visitorId,
      shopId: environment.shopId || 'default',
      gclid: attribution.gclid,
      originalGclid: originalGclid,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      referrer: attribution.referrer,
      startTime: new Date().toISOString(),
      landingPage: window.location.href,
      totalEvents: 0,
      hasConversion: false
    };
  }

  private startSession(): void {
    // Fire-and-forget: Keine Blockierung der App-Initialisierung
    this.saveMinimalSessionAsync().catch(error => {
      this.logger.warn('📊 Analytics: Minimal session save failed (non-critical)', error);
    });
    this.bufferFullSession();
  }

  private setupConsentWatcher(): void {
    effect(() => {
      const hasConsent = this.websiteStateService.analyticsConsent();
      const unprocessedEvents = this.websiteStateService.getUnprocessedEvents();
      const unprocessedSessions = this.websiteStateService.getUnprocessedSessions();


      this.logger.log("Ruuning effect - hasConsent:", hasConsent, "unprocessedEvents:", unprocessedEvents.length, "unprocessedSessions:", unprocessedSessions.length);
      if (hasConsent) {
        this.logger.log('🎯 Analytics consent granted - processing buffered data:', {
          events: unprocessedEvents.length,
          sessions: unprocessedSessions.length
        });

        if (unprocessedEvents.length > 0) {
          this.processBufferedEventsAsync();
        }
        if (unprocessedSessions.length > 0) {
          this.processBufferedSessionsAsync();
        }
      }
    });
  }

  private processBufferedSessionsAsync(): void {
    const unprocessedSessions = this.websiteStateService.getUnprocessedSessions();
    this.logger.log('📊 Processing', unprocessedSessions.length, 'unprocessed buffered sessions (non-blocking)');

    if (unprocessedSessions.length === 0) return;

    // Fire-and-forget: Starte Verarbeitung ohne auf Ergebnis zu warten
    this.processBatchedSessionsInBackground(unprocessedSessions).then(() => {
      this.logger.log('Done processing sessions in background');
    })
      .catch(error => {
        this.logger.warn('📊 Analytics: Background session processing failed (non-critical)', error);
        // Fallback: Versuche individuelle Verarbeitung
        this.processIndividualSessionsInBackground(unprocessedSessions);
      });
  }

  private processBufferedEventsAsync(): void {
    const unprocessedEvents = this.websiteStateService.getUnprocessedEvents();
    this.logger.log('📊 Processing', unprocessedEvents.length, 'unprocessed buffered events (non-blocking)');

    if (unprocessedEvents.length === 0) return;

    // Fire-and-forget: Starte Verarbeitung ohne auf Ergebnis zu warten
    this.processBatchedEventsInBackground(unprocessedEvents)
      .catch(error => {
        this.logger.warn('📊 Analytics: Background event processing failed (non-critical)', error);
        // Fallback: Versuche individuelle Verarbeitung
        this.processIndividualEventsInBackground(unprocessedEvents);
      });
  }

  private processBatchedSessionsInBackground(unprocessedSessions: any[]): Promise<void> {
    const sessionData = unprocessedSessions
      .filter(bufferedSession => this.shouldProcess())
      .map(bufferedSession => ({
        sessionType: 'full',
        sessionData: bufferedSession.sessionData
      }));

    if (sessionData.length > 0) {
      this.cloudFunctionsService.callFunction('save_analytics_batch', {
        shopId: environment.shopId || 'default',
        sessions: sessionData,
        events: []
      }).then((result) => {
        const processedSessionIds = unprocessedSessions.map(session => session.id);
        this.websiteStateService.markSessionsAsProcessed(processedSessionIds);
        this.logger.log('📊 Background batch processed', sessionData.length, 'sessions via Cloud Function', result);
      }).catch(error => {
        this.logger.warn('📊 Analytics: Background session batch failed (non-critical)', error);
        throw error;
      });
    }

    return Promise.resolve();
  }

  private processBatchedEventsInBackground(unprocessedEvents: any[]): Promise<void> {
    const eventData = unprocessedEvents
      .filter(bufferedEvent => this.shouldProcess())
      .map(bufferedEvent => ({
        eventData: this.createEventFromBuffered(bufferedEvent)
      }));

    if (eventData.length > 0) {
      // Fire-and-forget: Keine await, Promise wird nicht abgewartet
      this.cloudFunctionsService.callFunction('save_analytics_batch', {
        shopId: environment.shopId || 'default',
        sessions: [],
        events: eventData
      }).then((result) => {
        const processedEventIds = unprocessedEvents.map(event => event.id);
        this.websiteStateService.markEventsAsProcessed(processedEventIds);
        this.logger.log('📊 Background batch processed', eventData.length, 'events via Cloud Function', result);
      }).catch(error => {
        this.logger.warn('📊 Analytics: Background event batch failed (non-critical)', error);
        throw error;
      });
    }

    return Promise.resolve();
  }

  private processIndividualSessionsInBackground(unprocessedSessions: any[]): void {
    unprocessedSessions.forEach(bufferedSession => {
      if (this.shouldProcess()) {
        this.saveFullSessionAsync(bufferedSession.sessionData)
          .then(() => {
            this.websiteStateService.markSessionsAsProcessed([bufferedSession.id]);
            this.logger.log('📊 Background processed individual session');
          })
          .catch(error => {
            this.logger.warn('📊 Analytics: Failed to process individual session (non-critical)', error);
          });
      }
    });
  }

  private processIndividualEventsInBackground(unprocessedEvents: any[]): void {
    unprocessedEvents.forEach(bufferedEvent => {
      if (this.shouldProcess()) {
        Promise.resolve().then(() => {
          if (bufferedEvent.type === 'custom_event') {
            this.directTrackCustomEvent(bufferedEvent.eventName, bufferedEvent.eventData);
          } else if (bufferedEvent.type === 'page_view') {
            this.directTrackPageView(bufferedEvent.page);
          }
          this.websiteStateService.markEventsAsProcessed([bufferedEvent.id]);
          this.logger.log('📊 Background processed individual event');
        }).catch(error => {
          this.logger.warn('📊 Analytics: Failed to process individual event (non-critical)', error);
        });
      }
    });
  }

  private createEventFromBuffered(bufferedEvent: any): any {
    if (!this.currentSession) return bufferedEvent.eventData;

    return {
      eventId: this.generateEventId(),
      sessionId: this.currentSession.sessionId,
      visitorId: this.currentSession.visitorId,
      shopId: this.currentSession.shopId,
      eventType: bufferedEvent.eventData?.eventType || 'click',
      timestamp: new Date().toISOString(),
      page: bufferedEvent.eventData?.page || window.location.pathname,
      gclid: this.currentSession.gclid,
      originalGclid: this.currentSession.originalGclid,
      gclidAtEventTime: this.currentSession.gclid,
      isConversion: bufferedEvent.eventData?.isConversion || false,
      conversionType: bufferedEvent.eventData?.conversionType,
      conversionAction: bufferedEvent.eventData?.conversionAction,
      conversionValue: bufferedEvent.eventData?.conversionValue,
      contactMethod: bufferedEvent.eventData?.contactMethod,
      intentLevel: bufferedEvent.eventData?.intentLevel,
      elementId: bufferedEvent.eventData?.elementId,
      customData: bufferedEvent.eventData?.customData
    };
  }

  private shouldProcess(): boolean {
    if (environment.isAdminPage) return false;
    if (!isPlatformBrowser(this.platformId)) return false;
    if (!this.websiteStateService.analyticsConsent()) return false;
    if (!this.currentSession) return false;
    return true;
  }

  private bufferFullSession(): void {
    if (!this.currentSession) return;

    this.websiteStateService.addBufferedSession({
      id: this.generateBufferedSessionId(),
      sessionData: this.currentSession,
      timestamp: new Date().toISOString(),
      processed: false
    });

    this.logger.log('📊 Analytics: Full session buffered');
  }

  private directTrackCustomEvent(eventName: string, eventData: Partial<AnalyticsEvent>): void {
    if (!this.websiteStateService.analyticsConsent() || !this.currentSession) return;

    const event: AnalyticsEvent = {
      eventId: this.generateEventId(),
      sessionId: this.currentSession.sessionId,
      visitorId: this.currentSession.visitorId,
      shopId: this.currentSession.shopId,
      eventType: eventData.eventType || 'click',
      timestamp: new Date().toISOString(),
      page: eventData.page || window.location.pathname,
      gclid: this.currentSession.gclid,
      originalGclid: this.currentSession.originalGclid,
      gclidAtEventTime: this.currentSession.gclid,
      isConversion: eventData.isConversion || false,
      conversionType: eventData.conversionType,
      conversionAction: eventData.conversionAction,
      conversionValue: eventData.conversionValue,
      contactMethod: eventData.contactMethod,
      intentLevel: eventData.intentLevel,
      elementId: eventData.elementId,
      customData: eventData.customData
    };

    // Fire-and-forget: Direct save to Cloud Function (non-blocking)
    this.saveEventAsync(event).catch(error => {
      this.logger.warn('📊 Analytics: Event save failed (non-critical)', error);
    });

    // Update session counters
    this.currentSession.totalEvents++;
    if (event.isConversion) {
      this.currentSession.hasConversion = true;
    }
  }

  private directTrackPageView(url?: string): void {
    this.directTrackCustomEvent('page_view', {
      eventType: 'page_view',
      page: url || window.location.pathname
    });
  }

  private getOrCreateSessionIdentifiers(): { visitorId: string; sessionId: string } {
    if (!isPlatformBrowser(this.platformId)) {
      return {
        visitorId: 'server_visitor',
        sessionId: 'server_session'
      };
    }

    try {
      let visitorId = localStorage.getItem(ANALYTICS_CONFIG.visitorIdKey);
      if (!visitorId) {
        visitorId = 'v_' + Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        try {
          localStorage.setItem(ANALYTICS_CONFIG.visitorIdKey, visitorId);
          this.logger.log('📊 Analytics: New visitor ID created:', visitorId);
        } catch (storageError) {
          this.logger.warn('📊 Analytics: Could not save visitor ID to localStorage', storageError);
        }
      }

      let sessionId = sessionStorage.getItem(ANALYTICS_CONFIG.sessionIdKey);
      if (!sessionId) {
        sessionId = 's_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
        try {
          sessionStorage.setItem(ANALYTICS_CONFIG.sessionIdKey, sessionId);
          this.logger.log('📊 Analytics: New session ID created:', sessionId);
        } catch (storageError) {
          this.logger.warn('📊 Analytics: Could not save session ID to sessionStorage', storageError);
        }
      }

      return { visitorId, sessionId };
    } catch (error) {
      // Fallback falls localStorage komplett nicht verfügbar ist
      this.logger.warn('📊 Analytics: Storage not available, using fallback IDs', error);
      return {
        visitorId: 'fallback_v_' + Date.now(),
        sessionId: 'fallback_s_' + Date.now()
      };
    }
  }

  private getSimpleAttribution(): SimpleAttribution {
    if (!isPlatformBrowser(this.platformId)) return { referrer: 'direct' };

    const urlParams = new URLSearchParams(window.location.search);
    return {
      gclid: urlParams.get('gclid') || undefined,
      utm_source: urlParams.get('utm_source') || undefined,
      utm_medium: urlParams.get('utm_medium') || undefined,
      utm_campaign: urlParams.get('utm_campaign') || undefined,
      referrer: document.referrer || 'direct'
    };
  }

  private getOrSetOriginalGclid(currentGclid?: string): string | undefined {
    if (!isPlatformBrowser(this.platformId)) return undefined;

    try {
      const storedOriginalGclid = localStorage.getItem(ANALYTICS_CONFIG.originalGclidKey);
      const storedExpiry = localStorage.getItem(ANALYTICS_CONFIG.originalGclidExpiry);

      if (storedOriginalGclid && storedExpiry) {
        const expiryDate = new Date(storedExpiry);
        if (new Date() < expiryDate) {
          return storedOriginalGclid;
        } else {
          try {
            localStorage.removeItem(ANALYTICS_CONFIG.originalGclidKey);
            localStorage.removeItem(ANALYTICS_CONFIG.originalGclidExpiry);
          } catch (cleanupError) {
            this.logger.warn('📊 Analytics: Could not clean expired GCLID', cleanupError);
          }
        }
      }

      if (currentGclid) {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + ANALYTICS_CONFIG.attributionWindowDays);

        try {
          localStorage.setItem(ANALYTICS_CONFIG.originalGclidKey, currentGclid);
          localStorage.setItem(ANALYTICS_CONFIG.originalGclidExpiry, expiryDate.toISOString());
          this.logger.log('📊 Analytics: Stored original GCLID for 14 days:', currentGclid);
        } catch (storageError) {
          this.logger.warn('📊 Analytics: Could not store GCLID', storageError);
        }

        return currentGclid;
      }

      return undefined;
    } catch (error) {
      this.logger.warn('📊 Analytics: GCLID processing failed (non-critical)', error);
      return currentGclid;
    }
  }

  private generateEventId(): string {
    return 'e_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
  }

  private generateBufferedEventId(): string {
    return `evt_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private generateBufferedSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private saveMinimalSessionAsync(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.currentSession) {
      this.logger.warn('📊 Analytics: Cannot save minimal session - missing platform or session data');
      return Promise.resolve();
    }

    if (!this.currentSession.sessionId || !this.currentSession.visitorId) {
      this.logger.error('📊 Analytics: Cannot save minimal session - missing session identifiers', {
        sessionId: this.currentSession.sessionId,
        visitorId: this.currentSession.visitorId
      });
      return Promise.resolve();
    }

    const sessionData = {
      sessionId: this.currentSession.sessionId,
      visitorId: this.currentSession.visitorId,
      shopId: environment.shopId || 'default',
      sessionType: 'minimal',
      sessionData: {
        sessionId: this.currentSession.sessionId,
        visitorId: this.currentSession.visitorId,
        page: window.location.pathname
      }
    };

    this.cloudFunctionsService.callFunction('save_analytics_session', sessionData)
      .then(() => {
        this.logger.log('📊 Analytics: Minimal session saved with consistent IDs:', {
          sessionId: this.currentSession?.sessionId,
          visitorId: this.currentSession?.visitorId
        });
      })
      .catch(error => {
        this.logger.warn('Analytics: Failed to save minimal session (non-critical)', error);
      });

    return Promise.resolve();
  }

  private saveFullSessionAsync(sessionData: any): Promise<void> {
    const requestData = {
      sessionId: sessionData.sessionId,
      visitorId: sessionData.visitorId,
      shopId: sessionData.shopId,
      sessionType: 'full',
      sessionData: this.removeUndefinedFields(sessionData)
    };

    this.cloudFunctionsService.callFunction('save_analytics_session', requestData)
      .then(() => {
        this.logger.log('📊 Full session saved via Cloud Function');
      })
      .catch(error => {
        this.logger.warn('Analytics: Failed to save full session (non-critical)', error);
      });

    return Promise.resolve();
  }

  private saveEventAsync(event: AnalyticsEvent): Promise<void> {
    const requestData = {
      eventId: event.eventId,
      sessionId: event.sessionId,
      visitorId: event.visitorId,
      shopId: event.shopId,
      eventData: this.removeUndefinedFields(event)
    };

    this.cloudFunctionsService.callFunction('save_analytics_event', requestData)
      .then(() => {
      })
      .catch(error => {
        this.logger.warn('Analytics: Failed to save event (non-critical)', error);
      });

    return Promise.resolve();
  }

  private removeUndefinedFields(obj: any): any {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    return cleaned;
  }

  ngOnDestroy(): void {
    try {
      Object.values(this.engagementTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      this.engagementTimers = {};

      this.logger.log('📊 Analytics: Service cleaned up');
    } catch (error) {
      this.logger.warn('📊 Analytics: Cleanup error (non-critical)', error);
    }
  }
}